import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  Modal,
  Heading,
  Overflow,
  Creatable,
  ErrorMsg,
  Paragraph,
} from '@studio/legacy-components';
import { evaluate } from '@studio/conditions';
import useToggle from 'next/hooks/use-toggle';
import { selectCurrentPageUrlOptions } from 'next/lib/selectors-options';
import { ConditionsShape } from 'next/entities/rules';

import { isValidUrl } from 'next/lib/schema';
import UrlListItem from './UrlListItem';
import { URLList, URLSelectWrapper, SelectField, URLNotice } from './styled';

export function TestUrlModalManager({ urls = [], conditions }) {
  const [visible, toggle] = useToggle(false);
  const [selectedUrls, setSelectedUrls] = useState([]);
  const [activeUrl, setActiveUrl] = useState(null);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);

  const handleTestClick = () => {
    setError(null);
    setWarning(null);

    const isActiveUrlValid = isValidUrl(activeUrl);

    if (!isActiveUrlValid) {
      setError(
        'Looks like you forgot the https:// or http:// at the beginning. We need the whole URL to make sure your test is accurate.'
      );
      return;
    }

    const urlHasBeenTested = selectedUrls.some(({ url }) => url === activeUrl);

    if (!urlHasBeenTested) {
      setActiveUrl(null);
      setSelectedUrls(currentUrls => [
        ...currentUrls,
        {
          url: activeUrl,
          qualified: evaluate(activeUrl, conditions),
        },
      ]);
    } else {
      setWarning('Looks like that URL has already been tested.');
    }
  };

  const handleRemoveClick = url => {
    setSelectedUrls(prevSelectedUrls =>
      prevSelectedUrls.filter(({ url: itemUrl }) => itemUrl !== url)
    );
  };

  const selected = { label: activeUrl, value: activeUrl };

  return (
    <>
      <Button kind="secondary" onClick={toggle}>
        Test URL targeting
      </Button>
      <Modal visible={visible} onClose={toggle} size="l">
        <Heading>Test URL targeting</Heading>
        <Overflow>
          <Paragraph>
            Enter a URL and click Test to see if the Pin Experience will be
            shown to users when they visit it. You can see results for up to 5
            URLs at a time.
          </Paragraph>
        </Overflow>
        <URLSelectWrapper>
          <SelectField>
            <Creatable
              createOptionPosition="first"
              formatCreateLabel={label => label}
              error={!!error}
              // FIXME: this is a workaround for specific situation where we want to
              // show always Select an option after a value is selected
              // Once we have our Typeahead component this should no longer be needed
              value={activeUrl === null ? null : selected}
              placeholder="Select an option..."
              options={urls}
              isDisabled={selectedUrls.length === 5}
              onChange={({ value }) => {
                setActiveUrl(value);
              }}
            />
            {error && <ErrorMsg>{error}</ErrorMsg>}
          </SelectField>
          <Button
            aria-label="test url"
            kind="positive"
            onClick={handleTestClick}
          >
            Test
          </Button>
        </URLSelectWrapper>
        {warning && <URLNotice>{warning}</URLNotice>}
        <URLList>
          {selectedUrls.map(({ url, qualified }) => (
            <UrlListItem
              key={url}
              url={url}
              qualified={qualified}
              onRemove={() => handleRemoveClick(url)}
            />
          ))}
        </URLList>
      </Modal>
    </>
  );
}

TestUrlModalManager.propTypes = {
  urls: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })
  ),
  conditions: ConditionsShape,
};

const mapStateToProps = state => ({
  urls: selectCurrentPageUrlOptions(state),
});

export default connect(mapStateToProps)(TestUrlModalManager);
